import React, { useState } from 'react';

interface ModalProps {
  onClose: () => void;
}

type DetailView = 'none' | 'terms' | 'privacy' | 'thirdParty' | 'marketing';

// 구글 앱스 스크립트 Web App URL
const WEBAPP_URL = "https://script.google.com/macros/s/AKfycbwVXPRdOduGlnCIw0GTwIP9oOQPWfScI2UvaZJRtnFKdhQtPuhWFu8OLsVtXy4fHbsDyw/exec"; 

// 디스코드 웹훅 URL
const DISCORD_WEBHOOK_URL = "https://discord.com/api/webhooks/1460900968668463106/n7O-vd4uAiICpgkgGu4m8oQBXvWHif8UWNif7R4XVXCx8_cAuxIIxp-VueWtG76m9KpJ";

const ConsultationModal: React.FC<ModalProps> = ({ onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    agreedAll: false,
    terms: false,
    privacy: false,
    thirdParty: false,
    marketing: false,
  });

  const [detailView, setDetailView] = useState<DetailView>('none');

  const handleAllAgree = (checked: boolean) => {
    setFormData({
      ...formData,
      agreedAll: checked,
      terms: checked,
      privacy: checked,
      thirdParty: checked,
      marketing: checked
    });
  };

  const handleSingleAgree = (key: keyof typeof formData, checked: boolean) => {
    const nextData = { ...formData, [key]: checked };
    nextData.agreedAll = nextData.terms && nextData.privacy && nextData.thirdParty && nextData.marketing;
    setFormData(nextData);
  };

  // Discord 알림 전송 함수
  const sendDiscordNotification = async (data: any) => {
    const now = new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
    const message = {
      content: "🔔 **새로운 TOEFL 30 JUMP 상담 신청이 도착했습니다!**",
      embeds: [{
        title: "상담 신청 상세 정보",
        color: 0xF472B6, // 핑크색 (Tailwind pink-400 계열)
        fields: [
          { name: "👤 이름", value: data.name, inline: true },
          { name: "📞 휴대폰번호", value: data.phone, inline: true },
          { name: "📅 신청일시", value: now, inline: false },
          { name: "📜 서비스 이용약관", value: data.terms ? "✅ 동의" : "❌ 미동의", inline: true },
          { name: "🔒 개인정보 수집", value: data.privacy ? "✅ 동의" : "❌ 미동의", inline: true },
          { name: "📢 마케팅 수신", value: data.marketing ? "✅ 동의" : "❌ 미동의", inline: true }
        ],
        footer: { text: "TOEFL 30 JUMP Landing Page Notification" }
      }]
    };

    try {
      await fetch(DISCORD_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(message)
      });
    } catch (error) {
      console.error("Discord Notification Error:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.terms || !formData.privacy) {
      alert('필수 약관(서비스 이용 약관, 개인정보 수집)에 동의해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. 구글 시트 데이터 전송
      const params = new URLSearchParams();
      params.append('name', formData.name);
      params.append('phone', formData.phone);
      params.append('terms', formData.terms.toString());
      params.append('privacy', formData.privacy.toString());
      params.append('marketing', formData.marketing.toString());

      await fetch(WEBAPP_URL, {
        method: 'POST',
        mode: 'no-cors',
        body: params,
      });

      // 2. 디스코드 알림 발송 (직접 전송)
      await sendDiscordNotification({
        name: formData.name,
        phone: formData.phone,
        terms: formData.terms,
        privacy: formData.privacy,
        marketing: formData.marketing
      });

      alert(`[신청 완료]\n${formData.name}님, 상담 신청이 정상적으로 접수되었습니다.\nTOEFL 80+를 향한 여정을 함께하겠습니다!`);
      onClose();
    } catch (error) {
      console.error("Submission error:", error);
      alert("신청 중 오류가 발생했습니다. 네트워크 상태를 확인하시거나 잠시 후 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDetailContent = () => {
    switch (detailView) {
      case 'terms':
        return (
          <div className="space-y-4 text-xs text-slate-500">
            <p><strong>제1조 (목적)</strong>: 본 약관은 TOEFL 30 JUMP가 운영하는 사이트에서 제공하는 인터넷 관련 서비스의 이용 및 서비스 이용자의 권리, 의무 및 책임 사항을 규정함을 목적으로 합니다.</p>
            <p><strong>제2조 (회원가입)</strong>: 이용자는 회사가 정한 양식에 따라 회원정보를 기입한 후 이 약관에 동의함으로써 회원가입을 신청합니다.</p>
            <p><strong>제3조 (서비스 중단)</strong>: 회사는 설비 보수점검, 교체 및 고장 등의 사유 발생 시 서비스 제공을 중단할 수 있습니다.</p>
            <p><strong>제4조 (회원의 의무)</strong>: 회원은 타인의 정보 도용 및 허위 정보 게시를 금지합니다.</p>
          </div>
        );
      case 'privacy':
        return (
          <div className="space-y-4 text-xs text-slate-500">
            <table className="w-full border-collapse border border-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="border border-slate-100 p-2">항목</th>
                  <th className="border border-slate-100 p-2">목적</th>
                  <th className="border border-slate-100 p-2">보유 기간</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-slate-100 p-2">이름, 휴대전화번호</td>
                  <td className="border border-slate-100 p-2">서비스 이용 본인 확인</td>
                  <td className="border border-slate-100 p-2">회원 탈퇴 시까지</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      case 'thirdParty':
        return (
          <div className="space-y-2 text-xs text-slate-500">
            <p><strong>제공받는 자</strong>: 협력 상담 센터</p>
            <p><strong>제공 목적</strong>: 1:1 고객 상담 및 학습 설계</p>
            <p><strong>제공 항목</strong>: 성명, 연락처</p>
            <p><strong>보유 기간</strong>: 상담 완료 후 즉시 파기 (법령 제외)</p>
          </div>
        );
      case 'marketing':
        return (
          <div className="space-y-2 text-xs text-slate-500">
            <p><strong>목적</strong>: 신규 서비스 홍보, 이벤트 정보 및 광고성 정보 전송</p>
            <p><strong>혜택</strong>: 무료 가이드북 및 할인 쿠폰 제공</p>
            <p><strong>철회 방법</strong>: 고객센터 문의를 통한 수신 거부</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-800/30 backdrop-blur-md">
      <div className="absolute inset-0" onClick={onClose}></div>
      
      <div className="bg-[#FAF7F2] w-full max-w-md rounded-[3.5rem] overflow-hidden shadow-2xl relative animate-fade-in-up border border-[#F2ECE4]">
        {detailView !== 'none' ? (
          <div className="p-12 animate-fade-in-up">
            <button 
              onClick={() => setDetailView('none')}
              className="mb-6 flex items-center text-pink-300 font-bold text-sm"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              뒤로가기
            </button>
            <h4 className="text-xl font-black text-slate-800 mb-6">
              {detailView === 'terms' && '서비스 이용 약관'}
              {detailView === 'privacy' && '개인정보 수집 및 이용 동의'}
              {detailView === 'thirdParty' && '개인정보 제3자 제공 동의'}
              {detailView === 'marketing' && '마케팅 정보 수신 동의'}
            </h4>
            <div className="max-h-[300px] overflow-y-auto pr-2 bg-white p-6 rounded-2xl border border-pink-50">
              {renderDetailContent()}
            </div>
          </div>
        ) : (
          <div className="p-10 md:p-12">
            <button 
              onClick={onClose}
              className="absolute top-10 right-10 text-slate-400 hover:text-slate-600 transition-colors p-2"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="mb-10">
              <h3 className="text-3xl font-black text-slate-800 mb-2 tracking-tight">상담 신청하기</h3>
              <p className="text-slate-400 font-medium text-sm">30회 만에 바뀌는 당신의 미래, 지금 시작하세요.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Name</label>
                <input 
                  type="text" 
                  required
                  disabled={isSubmitting}
                  className="w-full px-6 py-4 rounded-2xl bg-white border border-[#F2ECE4] focus:border-pink-200 focus:outline-none transition-all placeholder:text-slate-200 font-bold text-slate-700 disabled:opacity-50"
                  placeholder="성함을 입력해주세요"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Phone</label>
                <input 
                  type="tel" 
                  required
                  disabled={isSubmitting}
                  className="w-full px-6 py-4 rounded-2xl bg-white border border-[#F2ECE4] focus:border-pink-200 focus:outline-none transition-all placeholder:text-slate-200 font-bold text-slate-700 disabled:opacity-50"
                  placeholder="연락처를 입력해주세요"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>

              <div className="pt-6 space-y-3 border-t border-[#F2ECE4] mt-6">
                <label className="flex items-center space-x-3 mb-2">
                  <input 
                    type="checkbox" 
                    className="w-5 h-5 rounded text-pink-300 focus:ring-pink-100 border-[#F2ECE4]" 
                    checked={formData.agreedAll}
                    onChange={(e) => handleAllAgree(e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span className="font-bold text-slate-700 text-sm">전체 동의합니다</span>
                </label>

                <div className="space-y-2.5">
                  <div className="flex items-center justify-between group">
                    <label className="flex items-center space-x-3">
                      <input 
                        type="checkbox" 
                        className="w-4 h-4 rounded text-pink-300 focus:ring-pink-100 border-[#F2ECE4]"
                        checked={formData.terms}
                        onChange={(e) => handleSingleAgree('terms', e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span className="text-slate-500 text-xs">서비스 이용 약관 <span className="text-pink-300 font-bold">(필수)</span></span>
                    </label>
                    <button type="button" onClick={() => setDetailView('terms')} className="text-[10px] font-bold text-slate-300 hover:text-pink-300 underline">자세히 보기</button>
                  </div>

                  <div className="flex items-center justify-between group">
                    <label className="flex items-center space-x-3">
                      <input 
                        type="checkbox" 
                        className="w-4 h-4 rounded text-pink-300 focus:ring-pink-100 border-[#F2ECE4]"
                        checked={formData.privacy}
                        onChange={(e) => handleSingleAgree('privacy', e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span className="text-slate-500 text-xs">개인정보 수집 및 이용 <span className="text-pink-300 font-bold">(필수)</span></span>
                    </label>
                    <button type="button" onClick={() => setDetailView('privacy')} className="text-[10px] font-bold text-slate-300 hover:text-pink-300 underline">자세히 보기</button>
                  </div>

                  <div className="flex items-center justify-between group">
                    <label className="flex items-center space-x-3">
                      <input 
                        type="checkbox" 
                        className="w-4 h-4 rounded text-pink-300 focus:ring-pink-100 border-[#F2ECE4]"
                        checked={formData.thirdParty}
                        onChange={(e) => handleSingleAgree('thirdParty', e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span className="text-slate-500 text-xs">개인정보 제3자 제공 동의</span>
                    </label>
                    <button type="button" onClick={() => setDetailView('thirdParty')} className="text-[10px] font-bold text-slate-300 hover:text-pink-300 underline">자세히 보기</button>
                  </div>

                  <div className="flex items-center justify-between group">
                    <label className="flex items-center space-x-3">
                      <input 
                        type="checkbox" 
                        className="w-4 h-4 rounded text-pink-300 focus:ring-pink-100 border-[#F2ECE4]"
                        checked={formData.marketing}
                        onChange={(e) => handleSingleAgree('marketing', e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span className="text-slate-500 text-xs">마케팅 정보 수신 동의 (선택)</span>
                    </label>
                    <button type="button" onClick={() => setDetailView('marketing')} className="text-[10px] font-bold text-slate-300 hover:text-pink-300 underline">자세히 보기</button>
                  </div>
                </div>
              </div>

              <button 
                type="submit"
                disabled={isSubmitting}
                className="w-full py-5 bg-pink-300 text-white font-black text-lg rounded-2xl shadow-lg hover:bg-pink-400 transition-all active:scale-95 mt-6 disabled:bg-slate-300 flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    제출 중...
                  </>
                ) : '상담 신청하기'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsultationModal;